/**
 * Canvas Empty Stage — interactive overlay shown when the active page has
 * no page sections or shared navigation/footer. Mounted as a sibling of the
 * iframe inside .pb-iframe-wrap,
 * mirroring the GlassOverlay pattern.
 *
 * Public API:
 *   stage.mount(iframeWrapEl)       — insert into DOM, evaluate state, show/hide
 *   stage.unmount()                  — remove from DOM, drop listeners
 *   stage.show({ withIntro })        — make visible (intro only when withIntro && first ever)
 *   stage.hide()                     — fade out + display:none
 *   stage.isActive()                 — boolean
 */

import { createCanvasEmptyStageMarkup } from './canvas-empty-stage-markup.js';
import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { prefersReducedMotion } from './feedback.js';
import { normalizeSkinUrl } from '../shared/skin-url.js';
import { createUrlField } from './components.js';
import { isDeployedOnly } from './skin-wizard/skin-api.js';
import { isRealUser } from '../templates/auth-api.js';
// Call-time-only import from shell (same intentional module cycle toolbar.js
// uses): shell imports this module, but getCurrentUser is never referenced
// during module evaluation.
import { getCurrentUser } from './shell.js';

const INTRO_FLAG_KEY = 'pb-stage-intro-v1';
const SPRING = 'cubic-bezier(.2, .8, .2, 1)';

function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 5) return 'Good evening';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Good evening';
}

function isCanvasEmpty() {
  const state = store.getState?.();
  const page = store.getActivePage?.();
  if (!page) return true;
  const pageSectionCount = Array.isArray(page.sections) ? page.sections.length : 0;
  const shared = state?.sharedSections || {};
  return pageSectionCount === 0 && !shared.navigation && !shared.footer;
}

function isTypingTarget(target) {
  return target?.matches?.('input, textarea, select, [contenteditable="true"]');
}

/** The first-run stage temporarily owns rail visibility, never its saved preference or tab. */
export function bindEmptyStageRail(paletteEl) {
  let previousCollapsed = null;
  return eventBus.on('stage:active-changed', ({ active }) => {
    if (active) {
      if (previousCollapsed !== null) return;
      previousCollapsed = paletteEl.classList.contains('pb-collapsed');
      paletteEl.classList.add('pb-collapsed');
    } else {
      if (previousCollapsed === null) return;
      paletteEl.classList.toggle('pb-collapsed', previousCollapsed);
      previousCollapsed = null;
    }
    eventBus.emit('sidebar:state-changed', { open: !paletteEl.classList.contains('pb-collapsed') });
  });
}

export class CanvasEmptyStage {
  constructor() {
    this._stageEl = null;
    this._wrapEl = null;
    this._iframeEl = null;
    this._active = false;
    this._listeners = [];

    // rAF cache for cursor spotlight
    this._mx = 0;
    this._my = 0;
    this._rafQueued = false;

    // Intro animation tracking
    this._introPlayed = false;
    this._introRunning = false;
    this._introAnimations = [];
    this._introSkipHandler = null;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  mount(wrapEl) {
    if (this._stageEl) return;
    if (!wrapEl) return;

    const iframe = wrapEl.querySelector('iframe');
    if (!iframe) return;

    this._wrapEl = wrapEl;
    this._iframeEl = iframe;

    const el = document.createElement('div');
    el.className = 'pb-stage';
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', 'Get started');
    el.dataset.testid = 'canvas-empty-stage';
    el.hidden = true;
    el.append(createCanvasEmptyStageMarkup());
    iframe.insertAdjacentElement('afterend', el);
    this._stageEl = el;

    this._applyGreeting();
    this._bindDoorHandlers();
    this._bindKeyboardShortcuts();
    this._bindStateBus();
    this._bindCursorTracking();
    this._bindMorphBus();

    // Initial state check — mount fires AFTER shell's first render, so the
    // store already has the right state. Don't wait for state-changed.
    if (isCanvasEmpty()) {
      this.show({ withIntro: true });
    }
  }

  unmount() {
    this.hide();
    if (this._introSkipHandler) {
      document.removeEventListener('pointerdown', this._introSkipHandler, true);
      document.removeEventListener('keydown', this._introSkipHandler, true);
      this._introSkipHandler = null;
    }
    for (const a of this._introAnimations) {
      try {
        a.cancel();
      } catch {
        /* ignore */
      }
    }
    this._introAnimations = [];
    this._introRunning = false;

    for (const off of this._listeners) {
      try {
        off();
      } catch {
        /* ignore */
      }
    }
    this._listeners = [];

    if (this._stageEl?.parentNode) {
      this._stageEl.parentNode.removeChild(this._stageEl);
    }
    this._stageEl = null;
    this._wrapEl = null;
    this._iframeEl = null;
    this._active = false;
  }

  // ── Visibility ─────────────────────────────────────────────────────

  show({ withIntro = false } = {}) {
    if (!this._stageEl) return;
    if (this._active) return;

    this._active = true;
    this._stageEl.hidden = false;
    this._stageEl.style.pointerEvents = 'auto';
    document.body.classList.add('pb-stage-active');
    eventBus.emit('stage:active-changed', { active: true });

    if (withIntro && !this._introPlayed && !prefersReducedMotion() && !this._hasIntroFlag()) {
      this._runIntro();
    } else if (withIntro) {
      this._introPlayed = true;
    }
  }

  hide() {
    if (!this._stageEl) return;
    if (!this._active) return;

    // Skip any in-flight intro before fading out so animations don't race
    // a hide/unmount.
    if (this._introRunning) this._skipIntro();

    this._active = false;
    this._stageEl.hidden = true;
    this._stageEl.style.pointerEvents = 'none';
    document.body.classList.remove('pb-stage-active');
    eventBus.emit('stage:active-changed', { active: false });
  }

  isActive() {
    return this._active;
  }

  // ── Intro orchestration ─────────────────────────────────────────────

  _hasIntroFlag() {
    try {
      return Boolean(localStorage.getItem(INTRO_FLAG_KEY));
    } catch {
      return false;
    }
  }

  _markIntroFlag() {
    try {
      localStorage.setItem(INTRO_FLAG_KEY, '1');
    } catch {
      /* private mode / quota — no-op */
    }
  }

  _runIntro() {
    const stageEl = this._stageEl;
    if (!stageEl) return;

    this._introRunning = true;
    stageEl.classList.add('pb-stage-intro');

    const greeting = stageEl.querySelector('.pb-stage-greeting');
    const doors = stageEl.querySelectorAll('.pb-stage-door');
    const animations = [];

    // Greeting + subtitle — fade and rise together.
    if (greeting) {
      animations.push(
        greeting.animate(
          [
            { opacity: 0, transform: 'translateY(8px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          { duration: 360, delay: 460, easing: SPRING, fill: 'forwards' }
        )
      );
    }

    // Doors — staggered scale + lift.
    doors.forEach((el, i) => {
      animations.push(
        el.animate(
          [
            { opacity: 0, transform: 'translateY(14px) scale(0.96)' },
            { opacity: 1, transform: 'translateY(0)    scale(1)' }
          ],
          { duration: 420, delay: 700 + i * 80, easing: SPRING, fill: 'forwards' }
        )
      );
    });

    this._introAnimations = animations;
    this._introSkipHandler = (e) => {
      // Don't hijack the URL input or any other input typing.
      if (e.type === 'keydown' && isTypingTarget(e.target)) return;
      this._skipIntro();
    };
    document.addEventListener('pointerdown', this._introSkipHandler, { once: true, capture: true });
    document.addEventListener('keydown', this._introSkipHandler, { capture: true });

    const last = animations[animations.length - 1];
    Promise.resolve(last?.finished)
      .then(() => this._finishIntro())
      .catch(() => this._finishIntro());
  }

  _skipIntro() {
    if (!this._introRunning) return;
    for (const a of this._introAnimations) {
      try {
        a.finish();
      } catch {
        /* finished/cancelled — ignore */
      }
    }
  }

  _finishIntro() {
    if (!this._introRunning) return;
    this._introRunning = false;
    this._introPlayed = true;
    this._stageEl?.classList.remove('pb-stage-intro');

    for (const a of this._introAnimations) {
      try {
        a.cancel();
      } catch {
        /* ignore */
      }
    }
    this._introAnimations = [];

    if (this._introSkipHandler) {
      document.removeEventListener('pointerdown', this._introSkipHandler, true);
      document.removeEventListener('keydown', this._introSkipHandler, true);
      this._introSkipHandler = null;
    }
    this._markIntroFlag();
  }

  // ── FLIP morph (signature moment) ───────────────────────────────────
  //
  // Visual-continuity trick: when the user picks a section from Add Section,
  // the picker tile clones, flies from where they clicked to the wireframe
  // hero rect, and fades — revealing the real section that the iframe
  // re-rendered underneath. The stage is parent DOM and the section is in
  // the iframe, so this is *visual* continuity, not literal DOM continuity.

  _bindMorphBus() {
    const off = eventBus.on('stage:morph-from-rect', ({ rect, sourceEl }) => {
      if (!this._active || !rect || !sourceEl) return;
      if (prefersReducedMotion()) return;
      this._runFlipMorph({ rect, sourceEl });
    });
    this._listeners.push(off);
  }

  _runFlipMorph({ rect, sourceEl }) {
    // Target rect: a band at the top of the iframe canvas, sized like a real
    // hero section. The wireframe hero is only ~110px tall — using it as the
    // target would *shrink* the clone vertically instead of expanding it into
    // the frame. The iframe rect is the real "where the section appears".
    const iframeEl = this._iframeEl;
    if (!iframeEl) return;
    const iframeRect = iframeEl.getBoundingClientRect();
    if (!iframeRect.width || !iframeRect.height) return;

    const targetTop = iframeRect.top;
    const targetLeft = iframeRect.left;
    const targetWidth = iframeRect.width;
    // Hero-ish height: clamp to a sensible band so the clone feels like a
    // section landing, not a full-page swap.
    const targetHeight = Math.min(Math.max(rect.height * 2.4, 360), iframeRect.height * 0.7);

    const clone = sourceEl.cloneNode(true);
    clone.removeAttribute('data-testid');
    clone.style.cssText = `
      position: fixed;
      top: ${rect.top}px;
      left: ${rect.left}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      margin: 0;
      z-index: var(--pb-z-context-menu);
      pointer-events: none;
      transform-origin: top left;
      box-shadow: 0 18px 40px rgba(0, 120, 212, 0.28), 0 4px 12px rgba(15, 23, 42, 0.18);
      border-radius: var(--pb-radius-md);
      overflow: hidden;
      background: var(--pb-surface);
      will-change: transform, opacity;
    `;
    document.body.appendChild(clone);

    const dx = targetLeft - rect.left;
    const dy = targetTop - rect.top;
    const sx = targetWidth / rect.width;
    const sy = targetHeight / rect.height;

    const flight = clone.animate(
      [
        { transform: 'translate(0, 0) scale(1, 1)' },
        { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` }
      ],
      { duration: 520, easing: SPRING, fill: 'forwards' }
    );

    flight.finished
      .then(() => {
        const fade = clone.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 220,
          easing: 'ease-out',
          fill: 'forwards'
        });
        return fade.finished;
      })
      .catch(() => {
        /* cancellation — fall through to cleanup */
      })
      .finally(() => {
        try {
          clone.remove();
        } catch {
          /* ignore */
        }
      });
  }

  // ── Internals ──────────────────────────────────────────────────────

  _applyGreeting() {
    const prefix = this._stageEl?.querySelector('.pb-stage-greeting-prefix');
    if (prefix) prefix.textContent = timeOfDayGreeting();
  }

  _bindDoorHandlers() {
    const stageEl = this._stageEl;

    stageEl.querySelector('[data-door="1"]')?.addEventListener('click', () => {
      eventBus.emit('palette:open-add-section');
    });

    stageEl.querySelector('[data-door="2"]')?.addEventListener('click', () => {
      eventBus.emit('toolbar:open-templates');
    });

    const door3 = stageEl.querySelector('[data-door="3"]');

    // SW-3 / S3. The second consumer that earns the primitive: this door and
    // the wizard's step 1 are the same question, and until now each had its own
    // input and its own error line that disagreed about where the message goes.
    //
    // The field keeps this door's SIZING through `inputClass` — the geometry is
    // canvas-empty-stage.css's and stays there — and its own `normalizeSkinUrl`
    // import, which `skin-safety-source-tests.js` pins by specifier. The
    // emission is byte-for-byte what it was.
    const urlField = createUrlField({
      label: 'Client website URL',
      normalize: normalizeSkinUrl,
      placeholder: 'yourclient.com',
      testid: 'stage-door-url',
      // NO leading glyph here, deliberately. S3 says the door adopts the field
      // and keeps its own size and placement — and this door's art treatment is
      // a wide CENTRED monospace address, which a leading glyph would unbalance.
      // What it adopts is the behaviour: the live echo, the named refusal and
      // the Enter contract, all of which it previously hand-rolled or lacked.
      icon: false,
      inputClass: 'pb-stage-door-url-input',
      className: 'pb-stage-door-url-field',
      onSubmit: (url) => {
        eventBus.emit('toolbar:skin-from-url', { url, autoSubmit: true });
      }
    });
    // The visible label is redundant beside the door's own title and
    // description, so it is hidden VISUALLY and kept for the accessible name —
    // which is what the bare `aria-label` on the old input was doing, less well.
    urlField.el.querySelector('.pb-field-label')?.classList.add('pb-sr-only');
    const urlInput = urlField.input;
    stageEl.querySelector('.pb-stage-door-art-url')?.prepend(urlField.el);

    // "Open a saved project" — only meaningful for a signed-in user with cloud
    // projects. Hidden for anon (cloud endpoints would 401 and the account
    // panel that normally hosts this entry point isn't shown either).
    const openProjectBtn = stageEl.querySelector('.pb-stage-open-project');
    openProjectBtn?.addEventListener('click', () => {
      eventBus.emit('toolbar:open-projects');
    });

    // Deployed builds gate compose behind login: show the gate before the
    // user types. Live-swaps on auth:state (boot check, login, logout).
    const signinBtn = stageEl.querySelector('.pb-stage-door-signin');
    const syncAuthAffordances = () => {
      let isReal = false;
      try {
        isReal = isRealUser(getCurrentUser());
      } catch {
        /* auth state unavailable — treat as anon */
      }
      const needsSignIn = isDeployedOnly() && !isReal;
      // The WRAPPER, not the input: hiding only the control would leave its
      // label, leading glyph and echo line floating above the sign-in button.
      urlField.el.hidden = needsSignIn;
      if (signinBtn) signinBtn.hidden = !needsSignIn;
      if (openProjectBtn) openProjectBtn.hidden = !isReal;
    };
    signinBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      eventBus.emit('toolbar:auth-show');
    });
    syncAuthAffordances();
    const offAuth = eventBus.on('auth:state', syncAuthAffordances);
    this._listeners.push(offAuth);

    // Clicking anywhere on Door 3's body (outside the input) focuses the
    // input (or the sign-in button when gated).
    door3?.addEventListener('click', (e) => {
      if (e.target === urlInput || e.target === signinBtn) return;
      if (signinBtn && !signinBtn.hidden) {
        signinBtn.focus();
        return;
      }
      urlInput?.focus();
    });
  }

  _bindKeyboardShortcuts() {
    const handler = (e) => {
      if (!this._active) return;
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== '1' && e.key !== '2' && e.key !== '3') return;

      e.preventDefault();
      const stageEl = this._stageEl;
      if (e.key === '1') stageEl.querySelector('[data-door="1"]')?.click();
      else if (e.key === '2') stageEl.querySelector('[data-door="2"]')?.click();
      else if (e.key === '3') {
        const signin = stageEl.querySelector('.pb-stage-door-signin');
        if (signin && !signin.hidden) signin.focus();
        else stageEl.querySelector('.pb-stage-door-url-input')?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    this._listeners.push(() => document.removeEventListener('keydown', handler));
  }

  _bindStateBus() {
    const off = eventBus.on('state-changed', () => {
      const empty = isCanvasEmpty();
      if (empty && !this._active) this.show({ withIntro: false });
      else if (!empty && this._active) this.hide();
    });
    this._listeners.push(off);
  }

  _bindCursorTracking() {
    if (!this._stageEl) return;
    const onMove = (e) => {
      const rect = this._stageEl.getBoundingClientRect();
      this._mx = e.clientX - rect.left;
      this._my = e.clientY - rect.top;
      if (this._rafQueued) return;
      this._rafQueued = true;
      requestAnimationFrame(() => {
        if (this._stageEl) {
          this._stageEl.style.setProperty('--pb-stage-mx', `${this._mx}px`);
          this._stageEl.style.setProperty('--pb-stage-my', `${this._my}px`);
        }
        this._rafQueued = false;
      });
    };
    this._stageEl.addEventListener('mousemove', onMove);
    this._listeners.push(() => this._stageEl?.removeEventListener('mousemove', onMove));
  }
}
